import {Wish, Wishlist} from "@/api/types.ts";
import {Button, IconButton} from "@telegram-apps/telegram-ui";
import {CSSProperties, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {LuCopy, LuCopyCheck, LuEllipsisVertical} from "react-icons/lu";
import {TbPencil, TbSparkles} from "react-icons/tb";
import {useTranslation} from "react-i18next";
import {copyWish, deleteWish} from "@/api/apiMethods.ts";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useWishlist} from "@/providers/WishlistProvider.tsx";
import {haptic} from "@/helpers/haptic.ts";
import "./styles.css";

export const WishActionsMenu = (
  { wish, isOwner, onEditClick, onDelete, onUpdate, refreshWishlist, onCloseWish }:
  { wish: Wish | null, isOwner: boolean, onEditClick: () => void, onDelete?: (wishId: string) => void | Promise<void>, onUpdate: (wish: Wish) => void, refreshWishlist?: () => Promise<void>, onCloseWish?: () => void }
) => {
  const { t } = useTranslation();
  const { navigateToRoute, showSnackbar } = useNavigation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isCopyingLoading, setIsCopyingLoading] = useState<boolean>(false);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState<boolean>(false);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties | undefined>(undefined);

  let currentWishlist: Wishlist | undefined = undefined;
  try {
    const { wishlist } = useWishlist();
    currentWishlist = wishlist;
  } catch (e) { /* empty */ }

  const closeMenu = () => {
    if (!isOpen || isClosing) {
      return;
    }

    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, 120);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      closeMenu();
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  if (!wish) {
    return null;
  }

  const canCopy = !isOwner && !!currentWishlist;
  if (!isOwner && !canCopy) {
    return null;
  }

  const makeCopy = async () => {
    if (!currentWishlist || wish.isCopied) {
      return;
    }

    setIsCopyingLoading(true);
    try {
      await copyWish(currentWishlist.id, wish.id);
      haptic.notification('success');
      onUpdate({...wish, isCopied: true});
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
      if (refreshWishlist) await refreshWishlist();
      if (onCloseWish) onCloseWish();
    }
    setIsCopyingLoading(false);
  };

  const editWish = () => {
    closeMenu();
    onEditClick();
  };

  const removeWish = async () => {
    if (!wish) {
      return;
    }

    haptic.notification('warning');
    const isConfirmed = confirm(t("wish.dialog.delete.text"));
    if (!isConfirmed) {
      return;
    }

    setIsDeleteInProgress(true);
    try {
      await deleteWish(wish);
      haptic.notification('success');
      showSnackbar("🗑", "system.wishDeleted");
      closeMenu();
      if (onDelete) {
        await onDelete(wish.id);
      } else {
        if (refreshWishlist) await refreshWishlist();
        if (onCloseWish) onCloseWish();
      }
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
    }
    setIsDeleteInProgress(false);
  };

  const openGiftedPage = () => {
    closeMenu();
    const wishlistId = currentWishlist?.id;
    if (wishlistId) {
      navigateToRoute(`/main/wishlists/${wishlistId}/wishes/${wish.id}/gifted`);
      return;
    }
    navigateToRoute(`/main/wishes/all/${wish.id}/gifted`);
  };

  const updateMenuPosition = () => {
    const button = buttonRef.current;
    const host = button?.closest<HTMLElement>('[role="dialog"]');

    if (!button || !host) {
      setPortalHost(null);
      setMenuStyle(undefined);
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    setPortalHost(host);
    setMenuStyle({
      top: buttonRect.bottom - hostRect.top + 4,
      right: hostRect.right - buttonRect.right,
    });
  };

  const toggleMenu = () => {
    haptic.impact('soft');
    if (isOpen) {
      closeMenu();
      return;
    }

    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsClosing(false);
    updateMenuPosition();
    setIsOpen(true);
  };

  const menu = (
    <div
      ref={menuRef}
      className={"wish-actions__menu " + (isClosing ? "wish-actions__menu--closing" : "")}
      style={menuStyle}
      onClick={(event) => event.stopPropagation()}
    >
      {isOwner && (
        <>
          <Button
            className="wish-actions__item"
            type="button"
            mode="plain"
            before={<TbPencil size={22}/>}
            onClick={editWish}
          >
            {t("action.edit")}
          </Button>

          <Button
            className="wish-actions__item"
            type="button"
            mode="plain"
            before={<TbSparkles size={22}/>}
            onClick={openGiftedPage}
          >
            {t("wish.gifted.action")}
          </Button>

          <Button
            className="wish-actions__item"
            type="button"
            mode="plain"
            size="s"
            loading={isDeleteInProgress}
            disabled={isDeleteInProgress}
            onClick={removeWish}
          >
            <div className="text-color-destroy">{t("wish.action.delete")}</div>
          </Button>
        </>
      )}

      {canCopy && (
        <Button
          className="wish-actions__item"
          type="button"
          mode="plain"
          before={wish.isCopied ? <LuCopyCheck size={20}/> : <LuCopy size={20}/>}
          loading={isCopyingLoading}
          disabled={isCopyingLoading || !!wish.isCopied}
          onClick={makeCopy}
        >
          {wish.isCopied ? t("wish.alreadyCopied") : t("wish.action.copyWish")}
        </Button>
      )}
    </div>
  );

  return (
    <div className="wish-actions">
      <div ref={buttonRef}>
        <IconButton
          size="m"
          mode="plain"
          aria-label={t("action.actions", "Actions")}
          onClick={(event) => {
            event.stopPropagation();
            toggleMenu();
          }}
        >
          <LuEllipsisVertical size={24}/>
        </IconButton>
      </div>

      {isOpen && portalHost && menuStyle && createPortal(menu, portalHost)}
    </div>
  );
};
